/**
 * OaSIS Explorer Frontend - Broad Category Tag Component
 * 
 * Displays a broad category tag/chip with icon and label
 */

import React from 'react';
import { getBroadCategory, getBroadCategoryIcon } from '../../utils/occupationUtils';

interface BroadCategoryTagProps {
  oasisCode: string;
  className?: string;
  iconSize?: number;
  size?: 'sm' | 'md';
  variant?: 'default' | 'header';
}

/**
 * Broad category tag component
 * Displays the broad category label with icon in a styled badge
 */
export const BroadCategoryTag: React.FC<BroadCategoryTagProps> = ({
  oasisCode,
  className = '',
  iconSize,
  size = 'md',
  variant = 'default',
}) => {
  const broadCategory = getBroadCategory(oasisCode);
  
  if (!broadCategory) {
    return null;
  }

  // Set icon size based on component size if not explicitly provided
  const finalIconSize = iconSize ?? (size === 'sm' ? 10 : 12);
  // Get icon with appropriate styling based on variant
  const iconElement = getBroadCategoryIcon(broadCategory.value, finalIconSize);
  // Clone icon with updated className for header variant
  const icon = variant === 'header' && React.isValidElement(iconElement)
    ? React.cloneElement(iconElement as React.ReactElement<any>, {
        className: "text-white/90"
      })
    : iconElement;

  // Size-based styling
  const sizeClasses = size === 'sm' 
    ? 'gap-0.5 px-1.5 py-0.5 text-xs rounded'
    : 'gap-1 px-2.5 py-1 text-xs rounded-md';

  // Variant-based styling
  const variantClasses = variant === 'header'
    ? 'bg-white/20 backdrop-blur text-white/90'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';

  return (
    <div className={`inline-flex items-center ${sizeClasses} ${variantClasses} font-medium ${className}`}>
      {icon}
      <span>{broadCategory.label}</span>
    </div>
  );
};

